import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Contact } from '@/interfaces/contact'
import { listContactsQuery } from '@/queries/contact.query'
import { listDepartmentsQuery } from '@/queries/department.query'
import {
  listAgentTicketsQuery,
  listDeskTicketsQuery,
  listFinishedTicketsQuery,
  listPendingTicketsQuery,
} from '@/queries/ticket.query'
import { listWhatsappConnectionQuery } from '@/queries/whatsapp.query'
import { TicketService } from '@/services/ticket.service'
import {
  selectFocusTicket,
  selectSetActiveTab,
  selectSetDialog,
  useChatStore,
} from '@/stores/chat.store'
import { avatarName } from '@/utils/avatar-name'
import { IconCheck, IconX } from '@tabler/icons-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { useDebounce } from '@uidotdev/usehooks'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}
export function NewChatDialog({ onOpenChange, open }: Readonly<Props>) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const focusTicket = useChatStore(selectFocusTicket)
  const setActiveTab = useChatStore(selectSetActiveTab)
  const setDialog = useChatStore(selectSetDialog)
  const [selectedUsers, setSelectedUsers] = useState<Contact[]>([])
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('')
  const [selectedWhatsappId, setSelectedWhatsappId] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [filters, setFilters] = useState({
    pageSize: 20,
    name: '',
  })
  const debouncedFilters = useDebounce(filters, 500)
  const { data: contacts } = useQuery(listContactsQuery(debouncedFilters))
  const { data: departments } = useQuery(listDepartmentsQuery)
  const { data: whatsappConnections } = useQuery(listWhatsappConnectionQuery)

  const handleSelectUser = (user: Contact) => {
    if (!selectedUsers.some((selectedUser) => selectedUser.id === user.id)) {
      setSelectedUsers([...selectedUsers, user])
      return
    }

    handleRemoveUser(user.id)
  }

  const handleRemoveUser = (userId: number) => {
    setSelectedUsers(selectedUsers.filter((user) => user.id !== userId))
  }

  useEffect(() => {
    if (open) {
      if (!selectedDepartmentId && departments?.length) {
        setSelectedDepartmentId(String(departments[0].id))
      }

      if (!selectedWhatsappId && whatsappConnections?.length) {
        setSelectedWhatsappId(whatsappConnections[0].id)
      }

      return
    }

    setSelectedUsers([])
    setSelectedDepartmentId('')
    setSelectedWhatsappId('')
  }, [
    open,
    departments,
    selectedDepartmentId,
    selectedWhatsappId,
    whatsappConnections,
  ])

  const onSubmit = async () => {
    if (
      selectedUsers.length === 0 ||
      !selectedDepartmentId ||
      !selectedWhatsappId
    )
      return

    setIsSubmitting(true)

    try {
      const result = await TicketService.create({
        contactIds: selectedUsers.map((u) => u.id),
        departmentId: Number(selectedDepartmentId),
        whatsappId: selectedWhatsappId,
      })

      const firstTicket = result.created[0] || result.reused[0]
      if (!firstTicket) {
        toast.error(
          'Não foi possível iniciar o chat para os contatos selecionados.'
        )
        return
      }

      const joinedTicket = await TicketService.joinChat(firstTicket.id)

      await Promise.all([
        queryClient.invalidateQueries(listAgentTicketsQuery),
        queryClient.invalidateQueries(listDeskTicketsQuery),
        queryClient.invalidateQueries(listPendingTicketsQuery),
        queryClient.invalidateQueries(listFinishedTicketsQuery),
      ])

      onOpenChange(false)
      setDialog(null)
      setActiveTab('atendendo')
      focusTicket(joinedTicket.id)
      router.navigate({ to: '/chats' })
      toast.success('Chat aberto com sucesso')
    } catch {
      toast.error('Erro ao criar chat')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[600px]'>
        <DialogHeader>
          <DialogTitle>Novo chat</DialogTitle>
          <DialogDescription>
            Selecione os usuários para iniciar um novo chat
          </DialogDescription>
        </DialogHeader>
        <div className='flex flex-col gap-4'>
          <div className='grid gap-3 sm:grid-cols-2'>
            <div className='space-y-1.5'>
              <Label>Conexão WhatsApp</Label>
              <Select
                value={selectedWhatsappId}
                onValueChange={setSelectedWhatsappId}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Selecione uma conexão' />
                </SelectTrigger>
                <SelectContent>
                  {whatsappConnections?.map((connection) => (
                    <SelectItem key={connection.id} value={connection.id}>
                      {connection.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1.5'>
              <Label>Departamento</Label>
              <Select
                value={selectedDepartmentId}
                onValueChange={setSelectedDepartmentId}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Selecione um departamento' />
                </SelectTrigger>
                <SelectContent>
                  {departments?.map((department) => (
                    <SelectItem
                      key={department.id}
                      value={String(department.id)}
                    >
                      {department.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-sm opacity-80'>Para:</span>
            {selectedUsers.map((user) => (
              <Badge key={user.id} variant='default'>
                {user.name}
                <button
                  className='ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleRemoveUser(user.id)
                    }
                  }}
                  onClick={() => handleRemoveUser(user.id)}
                >
                  <IconX className='h-3 w-3 text-muted-foreground hover:text-foreground' />
                </button>
              </Badge>
            ))}
          </div>
          <Command className='rounded-lg border' shouldFilter={false}>
            <CommandInput
              placeholder='Pesquisar contatos...'
              className='text-foreground'
              value={filters.name}
              onValueChange={(value) => setFilters({ ...filters, name: value })}
            />
            <CommandList>
              <CommandEmpty>Nenhum contato encontrado.</CommandEmpty>
              <CommandGroup>
                {contacts?.data.map((user) => (
                  <CommandItem
                    key={user.id}
                    onSelect={() => handleSelectUser(user)}
                    className='flex items-center justify-between gap-2'
                  >
                    <div className='flex items-center gap-2'>
                      <Avatar className='h-8 w-8'>
                        <AvatarImage
                          src={user.profilePicUrl}
                          alt='Foto de perfil'
                        />
                        <AvatarFallback>{avatarName(user.name)}</AvatarFallback>
                      </Avatar>
                      <div className='flex flex-col'>
                        <span className='text-sm font-medium'>{user.name}</span>
                        <span className='text-xs opacity-80'>{user.phone}</span>
                      </div>
                    </div>

                    {selectedUsers.find((u) => u.id === user.id) && (
                      <IconCheck className='h-4 w-4' />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <Button
            variant={'default'}
            onClick={onSubmit}
            disabled={
              selectedUsers.length === 0 ||
              isSubmitting ||
              !selectedDepartmentId ||
              !selectedWhatsappId
            }
          >
            {isSubmitting ? 'Criando...' : 'Criar chat'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
